import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  AssetMetaInterface,
  AutoFlowArgs,
  AutoFlowResponse,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {useTaopix} from '../../Context/TaopixContext';
import {BaseAssistant} from './BaseAssistant';
import {useExperience} from '../../Experience/ExperienceProvider';
import {AssistantActions} from '../../Context/Assistant/AssistantActionTypes';
import {useErrorBoundary} from 'react-error-boundary';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';
import {AssistantType} from '@taopix/taopix-core';

type AdvancedAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  onAfterRun: () => void;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

export const AdvancedAssistant = ({...props}: AdvancedAssistantProps) => {
  const taopix = useTaopix();
  const assistant = useAssistant();
  const {experience} = useExperience();
  const {t} = useTranslation();
  const {showBoundary} = useErrorBoundary();
  const stateManager = taopix.core.get('stateManager');

  const run = (): Promise<void | (ReducerState & AutoFlowResponse)> => {
    const args: AutoFlowArgs = {
      save: false,
      showLoading: false,
      createPreviewThumbnail: false,
      coverItems: assistant.formatCoverItems(),
      pageCount: -1,
      checkImageUsage: false,
      reassignPictures: true,
      fromCreation: true,
      sortOrder: experience.WIZARD.addPicturesStep.defaultSortOrder,
      sortDirection: 'asc',
      // If covers are empty then we need leave them empty.
      // Otherwise, covers will be populated from the coverItems.
      includeCovers: false,
      assignPageStyleToCover1: false,
      assignPageStyleToCover2: false,
      includeTextPageStyles: experience.WIZARD.advancedDesignAssistantOptions.includeTextLayouts,
      pageNumber: -1,
    };

    return stateManager
      .call<AutoFlowArgs, ReducerState & AutoFlowResponse>('autoflow', args)
      .then(response => {
        assistant.dispatch(AssistantActions.setHistoryID(response.historyAction));
        assistant.dispatch(AssistantActions.incrementRunCount());
        return response;
      })
      .catch(error => {
        const formattedError = {...error};
        if (500 === error?.response?.status) {
          // Display our error from the server.
          throw new Error(
            (formattedError.response.data.error = error.response.data.error
              .split(' ')
              .map(errorPart => {
                if (0 === errorPart.indexOf('str_')) {
                  return t(`${errorPart}`, {ns: ['autofill', '*']});
                }

                return errorPart;
              })
              .join(' '))
          );
        }

        showBoundary(formattedError);
      });
  };

  return <BaseAssistant run={run} {...props} assistantType={AssistantType.Advanced} />;
};
